import { z } from 'zod';
import { resourceKeySchema, timestampSchema, versionedSchema } from './common';

/**
 * Parties and contracts: somebody on the other side of the money.
 *
 * The cost ledger could already say that 240 000 € was committed against an
 * account on a date. It could not say who to, except as `supplierLabel` -- a
 * free-text string, frequently arriving from an import, spelled four ways
 * across four rows. "What have we committed to this contractor" was a question
 * the product could not answer, and "how much of this contract is left" was not
 * a question it could be asked at all.
 *
 * ## Two records, because they are two facts
 *
 * A **party** is somebody the organization deals with; a **contract** is one
 * agreement with one of them, for one project. Parties are organization-scoped
 * because a contractor is the same company on every project, and contracts are
 * project-scoped because an agreement is for an undertaking.
 *
 * ## A party has no kind, deliberately
 *
 * The obvious column is `kind: contractor | consultant | supplier | client`,
 * and it is wrong: the same company is a contractor on one project and a
 * supplier on the next, so the role is a property of the *relationship*, not of
 * the company. The contract is the relationship, and it already says what the
 * arrangement is. A kind on the party would be a field that is right in one
 * context and misleading in every other.
 *
 * ## The free text stays
 *
 * `CostEntry.supplierLabel` and `contractRef` are not replaced by these. They
 * are what the *source* said -- the name on the invoice, the reference in the
 * spreadsheet -- and that is provenance which has to survive a reconciliation
 * that disagrees with it. `partyId` and `contractId` are what the project
 * decided those strings mean. Two different facts, and collapsing them would
 * make an import unauditable.
 */

/* ---------------------------------------------------------------- parties */

export const partySchema = versionedSchema.extend({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  /**
   * The name on the paperwork, when it differs from what everybody calls them.
   * Null far more often than not, which is why it is separate: a register that
   * made people type "Rakennus Oy Esimerkki (Esimerkki)" into one field would
   * get "Esimerkki" and lose the half that matters legally.
   */
  legalName: z.string().nullable(),
  /** Company number, VAT id, Y-tunnus: whatever this jurisdiction quotes. */
  registrationNumber: z.string().nullable(),
  notes: z.string().nullable(),
  /** Contracts held with this party, so deleting one is an informed act. */
  contractCount: z.number().int().nonnegative(),
  /**
   * The client itself: the organization its own staff belong to. One per
   * deployment, and it cannot be deleted (docs/organizations-design.md).
   */
  isHost: z.boolean(),
  /**
   * Whether this organization's lead may invite only addresses an email-domain
   * rule sends here (docs/organizations-design.md section 3.5).
   */
  leadInvitesOwnDomainsOnly: z.boolean(),
  /** Accounts belonging to this organization. One with members cannot be deleted. */
  memberCount: z.number().int().nonnegative(),
  /**
   * The rest of what blocks a delete, so the screen can say so before anybody
   * tries: vehicles it operates, clean-vehicle agreements it supplies, and
   * email-domain rules that send new people to it.
   */
  vehicleCount: z.number().int().nonnegative(),
  agreementCount: z.number().int().nonnegative(),
  emailDomainCount: z.number().int().nonnegative(),
  /**
   * What a delete would quietly unlink rather than refuse: the rows whose
   * reference is `SET NULL`. Counted so the confirmation can say how many,
   * because once they are null nobody can ask afterwards.
   */
  detachedOnDelete: z.object({
    costEntries: z.number().int().nonnegative(),
    tasks: z.number().int().nonnegative(),
    documentRevisions: z.number().int().nonnegative(),
    meetingParticipants: z.number().int().nonnegative(),
  }),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Party = z.infer<typeof partySchema>;

const partyWritableFields = z.object({
  name: z.string().min(1).max(200),
  legalName: z.string().max(200).nullish(),
  registrationNumber: z.string().max(64).nullish(),
  notes: z.string().max(2000).nullish(),
  leadInvitesOwnDomainsOnly: z.boolean().optional(),
});

export const createPartySchema = partyWritableFields.extend({ key: resourceKeySchema }).strict();
export type CreateParty = z.infer<typeof createPartySchema>;

export const updatePartySchema = partyWritableFields
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateParty = z.infer<typeof updatePartySchema>;

/** The party as it is embedded in a contract or an entry: name and nothing else. */
export const partyRefSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
});
export type PartyRef = z.infer<typeof partyRefSchema>;

/* -------------------------------------------------------------- contracts */

/**
 * Where a contract is in its own life.
 *
 * Three, on the same reasoning as an access window's three. `draft` is being
 * negotiated, `active` is signed and can be posted against, `closed` is
 * finished and should not receive new commitments. Deliberately not a workflow
 * with approvals: a contract is signed outside this system, and modelling an
 * approval here would be recording a decision that happened elsewhere.
 */
export const CONTRACT_STATUSES = ['draft', 'active', 'closed'] as const;
export const contractStatusSchema = z.enum(CONTRACT_STATUSES);
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const contractSchema = versionedSchema.extend({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  key: resourceKeySchema,
  /** The number people quote in a meeting. Free text: it is the other side's. */
  reference: z.string().nullable(),
  title: z.string(),
  party: partyRefSchema,
  status: contractStatusSchema,

  /**
   * The agreed value, in the project currency's minor units, and nullable
   * because a framework agreement genuinely has none until it is called off.
   * A nullable value renders as "not stated"; a zero would render as a contract
   * worth nothing, which is a different and wrong statement.
   */
  valueMinor: z.number().int().nullable(),
  currency: z.string().nullable(),

  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),

  notes: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Contract = z.infer<typeof contractSchema>;

const contractWritableFields = z.object({
  title: z.string().min(1).max(200),
  reference: z.string().max(120).nullish(),
  partyId: z.string().uuid(),
  status: contractStatusSchema.optional(),
  valueMinor: z.number().int().nullish(),
  startsOn: z.string().date().nullish(),
  endsOn: z.string().date().nullish(),
  notes: z.string().max(2000).nullish(),
});

/** A contract that ends before it starts is not a contract. */
function refineTerm<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value) => {
      const record = value as { startsOn?: string | null; endsOn?: string | null };
      if (!record.startsOn || !record.endsOn) return true;
      return record.startsOn <= record.endsOn;
    },
    { message: 'endsOn must not precede startsOn', path: ['endsOn'] },
  );
}

export const createContractSchema = refineTerm(
  contractWritableFields.extend({ key: resourceKeySchema }).strict(),
);
export type CreateContract = z.infer<typeof createContractSchema>;

export const updateContractSchema = refineTerm(
  contractWritableFields.partial().extend({ version: z.number().int().nonnegative() }).strict(),
);
export type UpdateContract = z.infer<typeof updateContractSchema>;

export const contractListQuerySchema = z.object({
  status: contractStatusSchema.optional(),
  partyId: z.string().uuid().optional(),
});
export type ContractListQuery = z.infer<typeof contractListQuerySchema>;

/* --------------------------------------------------------------- position */

/**
 * What a contract has actually cost so far, against what it is worth.
 *
 * The reason the whole feature exists. Each figure is one of the three ledgers
 * summed over the entries pointing at this contract, and they are never added
 * together -- committed, spent and paid answer three different questions
 * (requirements 5.4), and a single "total" would be the answer to none of them.
 *
 * `remainingMinor` is the one derived number, and only the commitment ledger
 * can produce it: value minus committed is "how much of this agreement is still
 * unspoken for". Null when the contract has no stated value, because subtracting
 * from an unknown gives an unknown rather than a large positive number.
 */
export const contractPositionSchema = z.object({
  contractId: z.string().uuid(),
  committedMinor: z.number().int(),
  costMinor: z.number().int(),
  cashMinor: z.number().int(),
  remainingMinor: z.number().int().nullable(),
  /** Entries pointing at this contract, so an empty position reads as empty
   *  rather than as zero. */
  entryCount: z.number().int().nonnegative(),
});
export type ContractPosition = z.infer<typeof contractPositionSchema>;

/**
 * How much of the agreed value is committed, as a fraction.
 *
 * Null when there is nothing to be a fraction of. Not clamped: a contract
 * committed past its value is exactly the thing a reader has to see, and a
 * progress bar that stopped at 100% would hide it.
 */
export function contractUtilisation(
  position: ContractPosition,
  valueMinor: number | null,
): number | null {
  if (valueMinor === null || valueMinor === 0) return null;
  return position.committedMinor / valueMinor;
}
